import React from "react";

/**
 * PlanRadioGroup.jsx
 *
 * Accessible, styled radio switch for selecting a subscription plan.
 * TailwindCSS only, no external UI libs. Keyboard & screen-reader friendly.
 *
 * Props
 * - feature: {
 *     id, featureType, title, description, active,
 *     plans: [{ id, planType, priceUsd, durationDays }]
 *   }
 * - value: number | null            // currently selected plan id
 * - onChange: (planId: number) => void
 */
export default function PlanRadioGroup({ feature, value, onChange }) {
  const plans = feature?.plans ?? [];

  return (
    <div>
      {/* Header */}
      {/* <div className="mb-2 flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
            {feature?.title || "Choose a plan"}
          </h3>
          {feature?.description && (
            <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
              {feature.description}
            </p>
          )}
        </div>
      </div> */}

      {/* Radio Group Container */}
      <fieldset className="mt-3" aria-labelledby="plans-label">
        <legend id="plans-label" className="sr-only">
          Subscription plans
        </legend>

        <div
          role="radiogroup"
          className="grid grid-cols-1 gap-3 sm:grid-cols-3"
        >
          {plans.map((plan) => {
            const selected = value === plan.id;
            return (
              <label
                key={plan.id}
                className={[
                  "group relative flex cursor-pointer flex-col rounded-2xl border p-4 transition bg-slate-600",
                  selected
                    ? "border-blue-600 ring-2 ring-blue-600/30"
                    : "border-zinc-200 hover:border-zinc-300 dark:border-zinc-800 dark:hover:border-zinc-200",
                ].join(" ")}
              >
                <input
                  type="radio"
                  name="plan"
                  className="sr-only"
                  checked={selected}
                  onChange={() => onChange?.(plan.id)}
                  aria-checked={selected}
                  value={plan.id}
                />

                <div className="flex items-start justify-between">
                  <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                    {humanizePlan(plan.planType)}
                  </span>
                  <span
                    className={[
                      "rounded-full px-2 py-0.5 text-lg font-medium",
                      selected
                        ? "bg-blue-600 text-white"
                        : "bg-zinc-100 text-zinc-700 dark:bg-cyan-800 dark:text-zinc-200",
                    ].join(" ")}
                    aria-hidden="true"
                  >
                    {priceLabel(plan.priceUsd)}
                  </span>
                </div>

                <div className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
                  {durationLabel(plan.durationDays)}
                </div>

                {/* Selection indicator */}
                <div
                  className={[
                    "pointer-events-none absolute inset-0 rounded-2xl",
                    selected ? "ring-2 ring-blue-600/30" : "",
                  ].join(" ")}
                  aria-hidden="true"
                />
              </label>
            );
          })}
        </div>
      </fieldset>
    </div>
  );
}

function humanizePlan(planType) {
  switch (planType) {
    case "TRIAL":
      return "Free Trial";
    case "ONE_MONTH":
      return "One Month";
    case "THREE_MONTH":
      return "Three Months";
    case "ONE_YEAR":
      return "One Year";
    case "LIFETIME":
      return "Lifetime";
    default:
      return planType
        .toString()
        .replace(/_/g, " ")
        .toLowerCase()
        .replace(/\b\w/g, (m) => m.toUpperCase());
  }
}

function priceLabel(priceUsd) {
  if (priceUsd === 0) return "Free";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: priceUsd % 1 === 0 ? 0 : 2,
    }).format(priceUsd)+" USDC";
  } catch {
    return `$${priceUsd} USDC`;
  }
}

function durationLabel(days) {
  if (!days) return "";
  if (days === 1) return "1 day";
  if (days < 30) return `${days} days`;
  if (days % 365 === 0) return `${days / 365} year${days === 365 ? "" : "s"}`;
  if (days % 30 === 0) return `${days / 30} month${days === 30 ? "" : "s"}`;
  return `${days} days`;
}

/**
 * Example usage:
 *
 * const feature = {
 *   id: 3,
 *   featureType: "WALLET_PRICE_ALERTS",
 *   title: "Wallet Price Alerts",
 *   description: "Price alerts for tokens in your wallet.",
 *   active: true,
 *   plans: [
 *     { id: 10, planType: "TRIAL", priceUsd: 0, durationDays: 14 },
 *     { id: 11, planType: "ONE_MONTH", priceUsd: 1, durationDays: 30 },
 *     { id: 12, planType: "ONE_YEAR", priceUsd: 6, durationDays: 365 },
 *   ],
 * };
 *
 * function Parent() {
 *   const [planId, setPlanId] = React.useState(null);
 *   return (
 *     <PlanRadioGroup feature={feature} value={planId} onChange={setPlanId} />
 *   );
 * }
 */

